/**
 * 图形验证码浮层（2026-10-27 从「就地展开一行」改成弹窗）
 * -----------------------------------------------------------------------------
 * ## 为什么改
 * 上一版点「获取验证码」是在原位置**就地展开**一行图形码输入。真机反馈是
 * 「点一下突然冒出一行，分不清是发出去了还是没发」—— 因为这一行出现时
 * 没有任何「现在开始一个新动作」的信号，而它和短信码输入行长得一模一样、
 * 占的是同一个矩形位置，眼睛容易把它当成同一个框换了内容。
 *
 * 弹窗把这件事说清楚：**这是一个独立步骤，填完才发**。有标题、有说明、
 * 有明确的「取消 / 确认发送」两个出口 —— 关上就是没发，不会留下半截状态。
 *
 * ## 职责边界
 * 这里只管「取图 → 输码 → 交给人机校验凭据」。**发码不在这里做**：
 * `onSubmit` 由调用方实现（它还要管倒计时、Toast、成功后的行内反馈），
 * 组件只按结果分两条路：
 *   - resolve → 关浮层（成功的收尾由调用方在 resolve 前做完）
 *   - reject  → **自动换一张图**再提示原因
 *
 * 必须换图：图形码在服务端是 `GETDEL` 一次性消费的，失败时那张图已经作废，
 * 不换图用户重试必失败（`PHONE-006` 死循环）—— 这与上一版同一口径。
 *
 * ## 浮层要占一层返回守卫
 * 手机上没有「关闭浮层」的键，左滑 / 安卓返回走的都是 history back。
 * 不挂守卫就会穿到页面层，弹出「退出填写？」（2026-10-26 真机踩过）。
 * `useDismissBackGuard` 是**栈式**的，叠在改号弹窗之上也安全：先关本层。
 */
import { Input, Modal, Toast } from 'antd-mobile';
import { useCallback, useEffect, useState } from 'react';
import { describeApiError } from '../api/errors';
import { fetchPhoneCaptcha, type PhoneCaptcha } from '../api/h5';
import { useDismissBackGuard } from '../lib/use-dismiss-back-guard';

/** 图形码位数，与服务端 `CAPTCHA_LENGTH` 对齐 */
const CAPTCHA_LENGTH = 4;

export interface CaptchaSubmit {
  captchaId: string;
  captchaCode: string;
}

interface CaptchaModalProps {
  visible: boolean;
  onClose: () => void;
  /**
   * 用人机校验凭据去发码。**抛错即视为失败**（组件会换图并提示），
   * 正常返回则在返回前把成功提示做完 —— 组件随后就关掉浮层。
   */
  onSubmit: (payload: CaptchaSubmit) => Promise<void>;
}

export function CaptchaModal({ visible, onClose, onSubmit }: CaptchaModalProps) {
  const [captcha, setCaptcha] = useState<PhoneCaptcha | null>(null);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  useDismissBackGuard(visible, onClose);

  /** 取一张新图（打开时与「点图换一张」共用）。旧图在服务端已被消费，刷新即作废 */
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const next = await fetchPhoneCaptcha();
      setCaptcha(next);
      // 调试回显（仅本地/测试）：答案由服务端给出，省掉「肉眼识别一张故意画歪的图」
      setInput(next.debugCaptchaCode ?? '');
    } catch (error) {
      Toast.show({ icon: 'fail', content: describeApiError(error) });
    } finally {
      setLoading(false);
    }
  }, []);

  // 每次打开都重新取图：上次留下的图可能已过期，且用户关掉再打开本就是新一次尝试
  useEffect(() => {
    if (!visible) {
      setInput('');
      return;
    }
    void load();
  }, [visible, load]);

  const confirm = async () => {
    if (!captcha || busy || input.trim().length !== CAPTCHA_LENGTH) return;
    setBusy(true);
    try {
      await onSubmit({ captchaId: captcha.captchaId, captchaCode: input.trim() });
      onClose();
    } catch (error) {
      // 失败必换图（那张已在服务端被消费掉），否则用户重试必然还是同一个错
      await load();
      Toast.show({ icon: 'fail', content: describeApiError(error) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      visible={visible}
      title="短信验证码"
      onClose={onClose}
      closeOnMaskClick={!busy}
      bodyStyle={{ paddingTop: 8 }}
      content={
        <div className="captcha-modal">
          <p className="captcha-modal__hint">为防止短信被刷，请填写下图中的 4 位字符</p>
          {/* 图在左、输入在右（2026-10-28 真机反馈改）：先看图再答题，视线从左往右；
              输入框必须自带边框 —— 这里不能用页面通用的 `.field-box`，那是行式排版
              （无边框、透明底），搬进弹窗就变成一个看不见的输入框。 */}
          <div className="captcha-modal__row">
            <button
              type="button"
              className="phone-verify-captcha__img"
              disabled={loading}
              onClick={() => void load()}
              aria-label="看不清，换一张"
            >
              {captcha ? (
                <img src={captcha.image} alt="图形验证码" />
              ) : (
                <span className="captcha-modal__img-hint">{loading ? '加载中' : '点击重试'}</span>
              )}
            </button>
            <div className="captcha-modal__field">
              <Input
                value={input}
                maxLength={CAPTCHA_LENGTH}
                placeholder="填写图形码"
                onChange={(next) => setInput(next.toUpperCase())}
              />
            </div>
          </div>
          <p className="captcha-modal__hint">看不清可以点图片换一张</p>
        </div>
      }
      actions={[
        { key: 'cancel', text: '取消', disabled: busy, onClick: onClose },
        {
          key: 'send',
          text: busy ? '发送中…' : '确认发送',
          primary: true,
          disabled: busy || loading || input.trim().length !== CAPTCHA_LENGTH,
          onClick: () => void confirm(),
        },
      ]}
    />
  );
}
